import React from 'react';
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { Wand2, Eye, Code, ArrowRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface GeneratedArticleProps {
  contentGenerated: boolean;
  progress: number;
  currentStepText: string;
  generatedContentTitle: string;
  generatedContentPreview: string;
  showRawContent: boolean;
  onToggleRawContent: () => void;
  onContinue: () => void;
}

export const GeneratedArticle: React.FC<GeneratedArticleProps> = ({
  contentGenerated,
  progress,
  currentStepText,
  generatedContentTitle,
  generatedContentPreview,
  showRawContent,
  onToggleRawContent,
  onContinue
}) => {
  if (!contentGenerated) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="text-center mb-8"
      >
        <motion.div 
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.4 
          }}
          className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6"
        >
          <Wand2 className="h-10 w-10 text-primary animate-pulse" />
        </motion.div>
        
        <motion.h3 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="text-xl font-medium mb-2"
        >
          🛠️ Generating first draft
        </motion.h3>
        
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="text-muted-foreground mb-4"
        >
          We're putting your favorite idea into words...
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="w-full max-w-md mx-auto"
        >
          <Progress value={progress} className="h-2" />
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.4 }}
            className="text-sm text-muted-foreground mt-2 whitespace-pre-line text-center"
          >
            {currentStepText}
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6 max-w-3xl mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="bg-card rounded-lg p-6 border border-border/50"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-medium">{generatedContentTitle}</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={onToggleRawContent}
                >
                  {showRawContent ? <Eye className="h-4 w-4" /> : <Code className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{showRawContent ? 'Show formatted' : 'Show raw'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="prose prose-sm max-w-none">
          {showRawContent ? (
            <pre className="whitespace-pre-wrap text-sm">{generatedContentPreview}</pre>
          ) : (
            <div 
              className="text-sm"
              dangerouslySetInnerHTML={{ __html: generatedContentPreview }}
            />
          )}
        </div>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="flex justify-end gap-3"
      >
        <Button onClick={onContinue}>
          Continue to Scheduling
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </motion.div>
    </motion.div>
  );
}; 